import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import CronsTable from './components/CronsTable';
import useAdminCrons from './hooks/useAdminCrons';
import { RunAdminCronNowAPI } from '../../../api/AdminAPI';

// Espera antes de recarregar a lista após o disparo, para a linha "Running" já estar no log.
const REFRESH_AFTER_RUN_MS = 3000;

/**
 * AdminCrons - Saúde das rotinas automáticas (cron) da plataforma (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminCrons() para carregar o status de cada script de automação, lido
 *   dos logs do canal "cron" e dos arquivos de lock (status.php)
 * - Renderiza AreaTitle + estatísticas (total / com erro / atrasadas / sem registro) +
 *   botão "Atualizar" + tabela; sem filtros, atualização silenciosa a cada 60s
 * - Orquestra "Rodar agora" (ConfirmDialog -> RunAdminCronNowAPI -> Toast + refresh)
 */
function AdminCrons() {
  const {
    routines,
    daysScanned,
    logDirExists,
    stats,
    isLoading,
    error,
    refresh,
  } = useAdminCrons();

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [routineToRun, setRoutineToRun] = useState(null);
  const [launching, setLaunching] = useState(false);
  const refreshTimer = useRef(null);

  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  const handleRunCancel = () => {
    if (launching) return;
    setRoutineToRun(null);
  };

  const handleRunConfirm = () => {
    if (!routineToRun) return;
    const { label } = routineToRun;
    setLaunching(true);
    RunAdminCronNowAPI(routineToRun.script, (response) => {
      setLaunching(false);
      if (!response || response.success === false) {
        setToast({ open: true, message: response?.error || 'Não foi possível disparar a rotina. Tente novamente.', severity: 'error' });
        return;
      }
      setRoutineToRun(null);
      setToast({ open: true, message: `${label} disparada. O resultado aparece na lista em instantes.`, severity: 'success' });
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(refresh, REFRESH_AFTER_RUN_MS);
    });
  };

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Rotinas"
          Icon={<ScheduleOutlinedIcon />}
          Description="Última execução e resultado das rotinas automáticas, lidos dos logs do canal cron e dos arquivos de lock. A coluna Agendamento espelha o crontab configurado no painel da hospedagem"
        />
      </Box>

      {!logDirExists && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Diretório de logs não encontrado. Nenhuma execução recente pôde ser lida.
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Rotinas: <strong>{stats.total}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Com erro: <strong>{stats.withError}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Atrasadas: <strong>{stats.late}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sem registro: <strong>{stats.never}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Não agendadas: <strong>{stats.unscheduled}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Janela analisada: <strong>{daysScanned} dias</strong>
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={refresh}
          disabled={isLoading}
        >
          Atualizar
        </Button>
      </Box>

      <CronsTable routines={routines} isLoading={isLoading} error={error} onRunNow={setRoutineToRun} />

      <ConfirmDialog
        isOpen={Boolean(routineToRun)}
        title="Rodar agora"
        message={routineToRun ? `Rodar "${routineToRun.label}" agora? A rotina faz o mesmo que no horário agendado, inclusive enviar e-mails e mensagens de WhatsApp.` : ''}
        variant="warning"
        confirmLabel="Rodar agora"
        cancelLabel="Cancelar"
        onConfirm={handleRunConfirm}
        onCancel={handleRunCancel}
        loading={launching}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminCrons;
